import { Controller } from "@hotwired/stimulus"

// Type-ahead column picker. Replaces a <select> that listed every source column
// as an <option>: on a wide layout that was N pickers x M columns = thousands of
// idle <option> nodes. Here the option list is rendered into `list` only while
// the dropdown is open, so the idle DOM holds just the search input + hidden
// value. The visible text is the column label ("3 - Name"); the submitted value
// is the column position, carried in a hidden field whose `change` runs
// whatever action the call site passes (the editor marks its dirty-guard).
export default class extends Controller {
  static targets = ["search", "value", "list"]
  // `filterKey` names a key of an option's own data; `filter` is the value that
  // key must have to be offered. Whoever owns the constraint writes `filter`
  // (a mapping row's data type does), and a selection it excludes is cleared.
  static values = { options: Array, filterKey: String, filter: String }

  initialize() {
    this.activeIndex = -1
    this.filtered = []
    this.onDocPointer = this.onDocPointer.bind(this)
  }

  connect() {
    document.addEventListener("mousedown", this.onDocPointer)
  }

  disconnect() {
    document.removeEventListener("mousedown", this.onDocPointer)
  }

  open() {
    if (this.searchTarget.disabled) return
    this.render(this.available())
    this.show()
  }

  // The chevron behaves like a select's arrow: it opens the list, and closes it
  // again when it is already open. It focuses the search input on the way in so
  // typing narrows the list straight away.
  //
  // Bound to mousedown with the default prevented, not to click — the same
  // reason an option is: the search input's own blur hides the list on a
  // deferred timer, and on a human-paced click that timer fires between
  // mousedown and click. `toggle` would then read an already-hidden list and
  // re-open the one the user was dismissing. Preventing the default keeps
  // focus where it is, so there is no blur to race.
  toggle(event) {
    event.preventDefault()
    if (this.searchTarget.disabled) return
    if (!this.listTarget.hidden) return this.hide()

    this.searchTarget.focus()
    this.render(this.available())
    this.show()
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    const matches =
      q === ""
        ? this.available()
        : this.available().filter((o) => String(o.label).toLowerCase().includes(q))
    this.render(matches)
    this.show()
  }

  // The options the current constraint allows. No constraint means all of them,
  // and an option that carries no data for the key is never offered under one.
  available() {
    if (!this.hasFilterKeyValue || this.filterKeyValue === "") return this.optionsValue

    return this.optionsValue.filter((o) => o.data && String(o.data[this.filterKeyValue]) === this.filterValue)
  }

  // A new constraint arrives from whoever owns it (a mapping row's data type).
  // Handled as an event rather than a value callback so the cleared value is
  // in place synchronously with the change that caused it — a value callback
  // runs a mutation-observer tick later. Clearing does not dispatch `change`
  // of its own.
  constrain(event) {
    this.filterValue = event.detail.value
    this.hide()

    const picked = this.optionsValue.find((o) => String(o.value) === String(this.valueTarget.value))
    if (!picked || this.available().includes(picked)) return

    this.searchTarget.value = ""
    this.valueTarget.value = ""
  }

  // An option's own data changed at the source (a file-layout column was given
  // a new data type in the card above, before anything was saved). Re-seat that
  // option so the next open offers the list the source now describes, and drop
  // a selection the constraint no longer allows — matched on value, because the
  // options are re-parsed from the attribute on every read.
  retype(event) {
    const { value, data } = event.detail
    this.optionsValue = this.optionsValue.map((option) =>
      String(option.value) === String(value)
        ? { ...option, data: { ...(option.data || {}), ...data } }
        : option
    )

    const picked = String(this.valueTarget.value)
    if (picked === "" || this.available().some((option) => String(option.value) === picked)) return

    this.searchTarget.value = ""
    this.valueTarget.value = ""
  }

  keydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter":
        if (!this.listTarget.hidden && this.activeIndex >= 0) {
          event.preventDefault()
          this.choose(this.activeIndex)
        }
        break
      case "Escape":
        this.hide()
        break
    }
  }

  blur() {
    // Defer so an option's mousedown handler can run first when focus leaves.
    setTimeout(() => this.hide(), 0)
  }

  render(options) {
    this.filtered = options
    this.activeIndex = -1
    this.listTarget.innerHTML = ""

    if (options.length === 0) {
      const empty = document.createElement("li")
      empty.className = "px-3 py-1.5 text-zinc-400 dark:text-zinc-500"
      empty.textContent = this.listTarget.dataset.emptyText || ""
      this.listTarget.appendChild(empty)
      return
    }

    options.forEach((option, index) => {
      const item = document.createElement("li")
      item.dataset.value = option.value
      item.dataset.index = index
      item.setAttribute("role", "option")
      item.setAttribute("data-test-id", "combobox-option")
      item.className =
        "cursor-pointer px-3 py-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800"
      item.textContent = option.label
      // mousedown (not click) so the choice lands before the search input blurs.
      item.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.choose(index)
      })
      this.listTarget.appendChild(item)
    })
  }

  move(delta) {
    if (this.listTarget.hidden) this.filter()
    if (this.filtered.length === 0) return

    this.activeIndex = (this.activeIndex + delta + this.filtered.length) % this.filtered.length
    this.highlight()
  }

  highlight() {
    this.listTarget.querySelectorAll("[data-test-id='combobox-option']").forEach((item, index) => {
      const on = index === this.activeIndex
      item.classList.toggle("bg-zinc-100", on)
      item.classList.toggle("dark:bg-zinc-800", on)
      if (on) item.scrollIntoView({ block: "nearest" })
    })
  }

  choose(index) {
    const option = this.filtered[index]
    if (!option) return

    this.searchTarget.value = option.label
    // Per-option data (e.g. a customer number) rides onto the hidden field before
    // the change event, so a listener can read it the way it read <option> data.
    if (option.data) Object.assign(this.valueTarget.dataset, option.data)
    this.setValue(String(option.value))
    this.hide()
  }

  setValue(value) {
    if (this.valueTarget.value === value) return

    this.valueTarget.value = value
    this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  onDocPointer(event) {
    if (!this.element.contains(event.target)) this.hide()
  }

  show() {
    this.listTarget.hidden = false
    this.searchTarget.setAttribute("aria-expanded", "true")
  }

  hide() {
    this.listTarget.hidden = true
    this.searchTarget.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
  }
}
